import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MapPin, Search, Crosshair, Hash, Map as MapIcon, X, AlertTriangle, CheckCircle2, Loader2,
} from "lucide-react";
import api from "../lib/api";
import { createMap, tealPin, L } from "../lib/leafletMap";

// Wave 25.17 — Pin-drop modal now uses Leaflet + OSM (free) instead of
// Google Maps JS SDK. The address autocomplete in the floating search
// box hits our backend /places/autocomplete which is itself backed by
// Nominatim (also free). Net: zero Google Maps API calls anywhere on
// this surface.

const SOURCE_LABEL = {
  places: { label: "Verified", color: "#10B981", icon: CheckCircle2 },
  pin: { label: "Pinned", color: "#1FB6C1", icon: MapIcon },
  pluscode: { label: "Plus Code", color: "#A78BFA", icon: Hash },
  coords: { label: "Coords", color: "#A78BFA", icon: Crosshair },
  manual: { label: "Manual", color: "#F59E0B", icon: AlertTriangle },
  legacy: { label: "Legacy", color: "#647087", icon: AlertTriangle },
};

const DD_RE = /^\s*(-?\d+(?:\.\d+)?)[, ]\s*(-?\d+(?:\.\d+)?)\s*$/;
// Plus Code (a.k.a Open Location Code): "<8 alphanumeric>+<2-3 alphanumeric>" optionally with locality
// e.g. "2HCM+W6", "2HCM+W6 Hazyview"
const PLUS_RE = /^[2-9CFGHJMPQRVWX]{4,8}\+[2-9CFGHJMPQRVWX]{2,3}(\s+\S.*)?$/i;

// Google Maps share link — covers maps.app.goo.gl, goo.gl/maps, and full
// google.com/maps URLs. Matches the most-used share formats only.
const GMAPS_LINK_RE = /^https?:\/\/(?:maps\.app\.goo\.gl|goo\.gl\/maps|(?:www\.)?google\.[a-z.]+\/maps)\//i;

/**
 * <PlaceInput />
 *
 * Multi-tier address picker. Output shape:
 *   { address, lat?, lng?, place_id?, source: "places"|"pin"|"pluscode"|"coords"|"manual"|"legacy" }
 *
 * Props:
 *   value          — controlled object (or null for empty)
 *   onChange(val)  — called with the full output above
 *   placeholder    — input placeholder (default: "Search address or business…")
 *   testId         — root data-testid prefix
 *   countryBias    — ISO code for autocomplete bias (default "za")
 *   compact        — bool, makes the input single-line (no helper row)
 *   required       — show red ring if empty
 */
export default function PlaceInput({
  value, onChange, placeholder = "Search address or business…",
  testId = "place-input", countryBias = "za", compact = false, required = false,
}) {
  const [q, setQ] = useState(value?.address || "");
  const [predictions, setPredictions] = useState([]);
  const [openDrop, setOpenDrop] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    setQ(value?.address || "");
  }, [value?.address]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenDrop(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchPredictions = (text) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!text || text.length < 2) { setPredictions([]); return; }
      // Try local parsers first — they short-circuit network calls
      if (GMAPS_LINK_RE.test(text)) { setPredictions([{ kind: "gmaps_link", primary: "Resolve Google Maps share link", secondary: text }]); return; }
      if (DD_RE.test(text)) { setPredictions([{ kind: "coords", primary: "Use these coordinates", secondary: text }]); return; }
      if (PLUS_RE.test(text)) { setPredictions([{ kind: "pluscode", primary: "Resolve Plus Code", secondary: text }]); return; }
      setLoading(true);
      try {
        const { data } = await api.get("/places/autocomplete", { params: { q: text, country: countryBias } });
        setPredictions(data.predictions || []);
      } catch (_e) { setPredictions([]); }
      setLoading(false);
    }, 250);
  };

  const handleType = (e) => {
    const text = e.target.value;
    setQ(text);
    setOpenDrop(true);
    fetchPredictions(text);
    // Mark unsynced — until user picks, store as manual
    if (!text) { onChange(null); return; }
    onChange({ address: text, source: "manual" });
  };

  const choosePlace = async (pred) => {
    setOpenDrop(false);
    setLoading(true);
    try {
      if (pred.kind === "coords") {
        const m = q.match(DD_RE);
        if (m) {
          const lat = parseFloat(m[1]);
          const lng = parseFloat(m[2]);
          // Reverse geocode for a friendly address
          let addr = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
          try {
            const { data } = await api.get("/places/reverse", { params: { lat, lng } });
            addr = data.address || addr;
          } catch (_e) { /* ignore */ }
          const out = { address: addr, lat, lng, source: "coords" };
          setQ(addr);
          onChange(out);
        }
      } else if (pred.kind === "gmaps_link") {
        // Resolve the share URL via the backend, which follows the redirect
        // and pulls coords / place_id / address out of the canonical URL.
        try {
          const { data } = await api.post("/trips/resolve-share-link", { url: q });
          // If we got coords but no name, try a reverse geocode for a nicer label.
          let addr = data.address || "";
          if (!addr && data.lat != null && data.lng != null) {
            try {
              const r = await api.get("/places/reverse", { params: { lat: data.lat, lng: data.lng } });
              addr = r.data.address || `${data.lat.toFixed(5)}, ${data.lng.toFixed(5)}`;
            } catch (_e) { addr = `${data.lat.toFixed(5)}, ${data.lng.toFixed(5)}`; }
          }
          const out = {
            address: addr,
            lat: data.lat,
            lng: data.lng,
            place_id: data.place_id,
            source: data.place_id ? "places" : (data.lat != null ? "coords" : "manual"),
          };
          setQ(addr);
          onChange(out);
        } catch (e) {
          alert(e?.response?.data?.detail || "Could not resolve that Google Maps link.");
        }
      } else if (pred.kind === "pluscode") {
        try {
          const { data } = await api.get("/places/pluscode", { params: { code: q } });
          const out = { address: data.address, lat: data.lat, lng: data.lng, place_id: data.place_id, source: "pluscode" };
          setQ(data.address);
          onChange(out);
        } catch (e) { /* keep input as-is */ }
      } else {
        // Real Place from Google
        const { data } = await api.get("/places/details", { params: { place_id: pred.place_id } });
        const out = {
          address: data.address || pred.full_text,
          lat: data.lat, lng: data.lng,
          place_id: data.place_id,
          name: data.name,
          phone: data.phone,
          website: data.website,
          source: "places",
        };
        setQ(out.address);
        onChange(out);
      }
    } catch (_e) { /* keep state */ }
    setLoading(false);
    setPredictions([]);
  };

  const onPinDropped = ({ lat, lng, address }) => {
    setQ(address);
    onChange({ address, lat, lng, source: "pin" });
    setPinOpen(false);
  };

  const sourceTag = value?.source ? SOURCE_LABEL[value.source] : null;
  const SourceIcon = sourceTag?.icon;
  const isVerified = value?.source && value.source !== "manual" && value.source !== "legacy";

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div className={`relative flex items-center ${required && !value?.address ? "ring-1 ring-rose-500/60 rounded" : ""}`}>
        <Search className="w-3.5 h-3.5 absolute left-2.5 text-[#647087] pointer-events-none" />
        <input
          value={q || ""}
          onChange={handleType}
          onFocus={() => q && setOpenDrop(true)}
          placeholder={placeholder}
          className="hv-input text-sm w-full pl-8 pr-20"
          data-testid={`${testId}-text`}
        />
        <div className="absolute right-1.5 flex items-center gap-1">
          {sourceTag && (
            <span
              className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded flex items-center gap-1"
              style={{ color: sourceTag.color, backgroundColor: `${sourceTag.color}15`, border: `1px solid ${sourceTag.color}40` }}
              title={`Source: ${value.source}`}
              data-testid={`${testId}-source`}
            >
              {SourceIcon && <SourceIcon className="w-2.5 h-2.5" />}
              {sourceTag.label}
            </span>
          )}
          <button
            type="button"
            onClick={() => setPinOpen(true)}
            className="p-1 rounded hover:bg-white/5 text-[#647087] hover:text-[#1FB6C1]"
            title="Drop a pin on the map"
            data-testid={`${testId}-pin-btn`}
          >
            <MapIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Dropdown */}
      {openDrop && predictions.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 hv-card max-h-[280px] overflow-y-auto shadow-xl"
          data-testid={`${testId}-dropdown`}
        >
          {loading && (
            <div className="p-2 text-xs text-[#647087] flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Searching…
            </div>
          )}
          {predictions.map((p, i) => (
            <button
              key={p.place_id || `${p.kind}-${i}`}
              type="button"
              onClick={() => choosePlace(p)}
              className="w-full text-left px-3 py-2 hover:bg-[#1fb6c1]/10 border-b border-white/5 last:border-0"
              data-testid={`${testId}-suggestion-${i}`}
            >
              <div className="flex items-center gap-2">
                {p.kind === "coords" ? <Crosshair className="w-3 h-3 text-[#A78BFA]" />
                  : p.kind === "pluscode" ? <Hash className="w-3 h-3 text-[#A78BFA]" />
                  : p.kind === "gmaps_link" ? <Search className="w-3 h-3 text-[#10B981]" />
                  : <MapPin className="w-3 h-3 text-[#1fb6c1]" />}
                <span className="text-sm font-medium">{p.primary || p.full_text}</span>
              </div>
              {p.secondary && <div className="text-[10px] text-[#647087] ml-5">{p.secondary}</div>}
            </button>
          ))}
        </div>
      )}

      {/* Helper row */}
      {!compact && (
        <div className="text-[10px] text-[#647087] mt-1 flex items-center gap-2 flex-wrap">
          <span>Search · pin · Plus Code · "lat, lng" · Google Maps share link</span>
          {value?.lat && value?.lng && (
            <span className="font-mono text-[#a0aabf]">
              {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
            </span>
          )}
          {value?.source === "manual" && value?.address && (
            <span className="text-amber-300 flex items-center gap-1" data-testid={`${testId}-manual-warn`}>
              <AlertTriangle className="w-2.5 h-2.5" /> Manual entry — drive time will need to be entered manually.
            </span>
          )}
        </div>
      )}

      {/* Pin-drop modal */}
      {pinOpen && (
        <PinDropModal
          initialLat={value?.lat}
          initialLng={value?.lng}
          onClose={() => setPinOpen(false)}
          onConfirm={onPinDropped}
        />
      )}
    </div>
  );
}

function PinDropModal({ initialLat, initialLng, onClose, onConfirm }) {
  // Default to centre of Mpumalanga (HidroVerse home turf) if no initial coords
  const startLat = initialLat ?? -25.4753;
  const startLng = initialLng ?? 30.9694;
  const [lat, setLat] = useState(startLat);
  const [lng, setLng] = useState(startLng);
  const [addr, setAddr] = useState("");
  const [resolving, setResolving] = useState(false);
  const [mapErr, setMapErr] = useState("");
  const [mapReady, setMapReady] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchDebounce = useRef(null);

  const mapDiv = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  // Always-current lat/lng in a ref so the (single-fire) map handler
  // can read fresh coords without re-binding listeners.
  const posRef = useRef({ lat: startLat, lng: startLng });

  // Reverse-geocode helper — turns coords into a friendly address.
  const reverseLookup = async (la, ln) => {
    setResolving(true);
    try {
      const { data } = await api.get("/places/reverse", { params: { lat: la, lng: ln } });
      setAddr(data.address || `${la.toFixed(5)}, ${ln.toFixed(5)}`);
    } catch (_e) {
      setAddr(`${la.toFixed(5)}, ${ln.toFixed(5)}`);
    }
    setResolving(false);
  };

  // Move marker / map / state in one go (no double reverse-geocode).
  const setPin = (la, ln, { panTo = true, lookup = true } = {}) => {
    posRef.current = { lat: la, lng: ln };
    setLat(la);
    setLng(ln);
    if (markerRef.current) {
      markerRef.current.setLatLng([la, ln]);
    }
    if (mapRef.current && panTo) {
      mapRef.current.panTo([la, ln]);
    }
    if (lookup) reverseLookup(la, ln);
  };

  // Boot the interactive Leaflet map once.
  useEffect(() => {
    let cancelled = false;
    try {
      if (!mapDiv.current) return undefined;
      const { map } = createMap(mapDiv.current, {
        center: [startLat, startLng],
        zoom: initialLat ? 15 : 9,
      });
      const marker = L.marker([startLat, startLng], {
        icon: tealPin(),
        draggable: true,
      }).addTo(map);

      // Click anywhere on the map → drop / move the pin
      map.on("click", (ev) => {
        if (!ev.latlng) return;
        setPin(ev.latlng.lat, ev.latlng.lng, { panTo: false });
      });
      // Marker drag end → update coords
      marker.on("dragend", () => {
        const p = marker.getLatLng();
        setPin(p.lat, p.lng, { panTo: false });
      });

      mapRef.current = map;
      markerRef.current = marker;
      setMapReady(true);
      // Initial reverse-geocode of the start position
      reverseLookup(startLat, startLng);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("PinDropModal: failed to load map", e);
      setMapErr("Failed to load the map. Check your network connection.");
    }
    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (_e) { /* ignore */ }
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search → backend /places/autocomplete (Nominatim)
  const handleSearchType = (e) => {
    const text = e.target.value;
    setSearchText(text);
    setSearchOpen(true);
    clearTimeout(searchDebounce.current);
    if (!text || text.length < 2) {
      setSearchResults([]);
      return;
    }
    searchDebounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/places/autocomplete", { params: { q: text, country: "za" } });
        setSearchResults(data.predictions || []);
      } catch (_e) {
        setSearchResults([]);
      }
    }, 300);
  };

  const choosePrediction = async (p) => {
    setSearchOpen(false);
    setSearchText(p.full_text || p.primary || "");
    let la = p.lat;
    let ln = p.lng;
    let label = p.full_text || p.primary || "";
    if (la == null || ln == null) {
      try {
        const { data } = await api.get("/places/details", { params: { place_id: p.place_id } });
        la = data.lat;
        ln = data.lng;
        label = data.address || label;
      } catch (_e) { /* keep */ }
    }
    if (la != null && ln != null && mapRef.current) {
      mapRef.current.setView([la, ln], 16);
      setPin(la, ln, { panTo: false, lookup: false });
      setAddr(label);
    }
  };

  // Manual lat/lng input → keep marker in sync
  const onLatChange = (v) => {
    const la = parseFloat(v);
    if (Number.isFinite(la)) setPin(la, posRef.current.lng);
    else setLat(v);
  };
  const onLngChange = (v) => {
    const ln = parseFloat(v);
    if (Number.isFinite(ln)) setPin(posRef.current.lat, ln);
    else setLng(v);
  };

  const onCoordsPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text");
    const m = text.match(DD_RE);
    if (m) {
      e.preventDefault();
      setPin(parseFloat(m[1]), parseFloat(m[2]));
    }
  };

  const useGeolocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (mapRef.current) mapRef.current.setView([pos.coords.latitude, pos.coords.longitude], 16);
        setPin(pos.coords.latitude, pos.coords.longitude);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return createPortal((
    <div
      // [Iter 290zp.28] z-[80] — pin-drop map is triggered from inside
      // a parent Modal (site-add / site-edit / site-picker) which uses
      // z-[70]. If the map's z-index is lower than or equal to the
      // parent modal, it opens BEHIND the modal and becomes unusable
      // (exact bug the user reported). Keep this comfortably above the
      // Modal z-index and any toasts / dropdowns.
      className="fixed inset-0 z-[80] bg-black/80 hv-safe-top hv-safe-bottom hv-safe-x flex flex-col"
      data-testid="pin-drop-modal"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 bg-[#0a0b0e] flex items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <MapIcon className="w-4 h-4 text-[#1FB6C1] shrink-0" />
          <h3 className="font-display text-sm md:text-base font-bold truncate">Drop a pin on the map</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="hv-btn hv-btn-ghost text-xs shrink-0"
          data-testid="pin-cancel"
        >
          <X className="w-3 h-3" /> Close
        </button>
      </div>

      {/* Map fills the remaining height; search box floats over top-left */}
      <div className="relative flex-1 min-h-0">
        {mapErr && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 text-xs text-rose-300 p-6 text-center">
            {mapErr}
          </div>
        )}
        {!mapReady && !mapErr && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 text-xs text-[#9aa3b5]">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading map…
          </div>
        )}
        <div ref={mapDiv} className="w-full h-full" data-testid="pin-map-canvas" />

        {/* Floating search box over the map */}
        <div className="absolute top-3 left-3 right-3 sm:right-auto sm:w-96 z-[1000]">
          <div className="relative shadow-2xl rounded-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0a0b0e]/70 pointer-events-none z-10" />
            <input
              type="text"
              value={searchText}
              onChange={handleSearchType}
              onFocus={() => searchResults.length && setSearchOpen(true)}
              placeholder="Search address or business…"
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-md bg-white text-[#0a0b0e] placeholder-[#0a0b0e]/50 border border-black/10 focus:outline-none focus:ring-2 focus:ring-[#1FB6C1]"
              data-testid="pin-search"
            />
            {searchOpen && searchResults.length > 0 && (
              <div className="absolute left-0 right-0 mt-1 bg-white text-[#0a0b0e] rounded-md shadow-2xl max-h-72 overflow-y-auto border border-black/10">
                {searchResults.map((p, i) => (
                  <button
                    key={p.place_id || i}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); choosePrediction(p); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#1FB6C1]/10 border-b border-black/5 last:border-0"
                    data-testid={`pin-search-result-${i}`}
                  >
                    <div className="text-sm font-medium">{p.primary || p.full_text}</div>
                    {p.secondary && <div className="text-[10px] text-[#647087]">{p.secondary}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="mt-1.5 px-2 text-[10px] text-white/80 hidden md:block">
            <span className="font-bold text-[#1FB6C1]">Tip:</span> Click anywhere on the map to drop the pin · Drag the pin to fine-tune.
          </div>
        </div>
      </div>

      {/* Footer — coords, address resolved, actions */}
      <div className="border-t border-white/10 bg-[#0a0b0e] shrink-0 p-3 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label>
            <div className="text-[10px] uppercase tracking-wider text-[#9aa3b5] mb-1">Latitude</div>
            <input
              type="number"
              step="any"
              value={lat}
              onChange={(e) => onLatChange(e.target.value)}
              onPaste={onCoordsPaste}
              className="hv-input text-sm w-full"
              data-testid="pin-lat-input"
            />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-[#9aa3b5] mb-1">Longitude</div>
            <input
              type="number"
              step="any"
              value={lng}
              onChange={(e) => onLngChange(e.target.value)}
              onPaste={onCoordsPaste}
              className="hv-input text-sm w-full"
              data-testid="pin-lng-input"
            />
          </label>
        </div>
        <div className="text-[11px] text-[#dde3ec] flex items-start gap-2 leading-snug">
          <Crosshair className="w-3 h-3 text-[#1FB6C1] mt-0.5 shrink-0" />
          <span><span className="text-[#9aa3b5]">Resolved:</span> {resolving ? "…" : (addr || "(unknown)")}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={useGeolocation}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="pin-use-my-location"
          >
            <Crosshair className="w-3 h-3" /> Use my location
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ lat, lng, address: addr || `${lat.toFixed(5)}, ${lng.toFixed(5)}` })}
            className="hv-btn hv-btn-primary text-xs ml-auto"
            data-testid="pin-confirm"
          >
            <CheckCircle2 className="w-3 h-3" /> Use this location
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
